import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Chip, Skeleton, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import { useColorMode } from "../../../theme/ColorModeContext";
import axiosInstance from "../../../services/axiosInstance";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

// ── Thresholds ────────────────────────────────────────────────
const WAIT_WARNING_SEC = 60;
const WAIT_DANGER_SEC  = 120;
const POLL_MS          = 2500;

// ── Agent status palette ──────────────────────────────────────
const AGENT_STATUS_CFG = {
  offline:   { color: "#9CA3AF", bg: "rgba(156,163,175,0.10)", label: "Offline"   },
  available: { color: "#22C55E", bg: "rgba(34,197,94,0.10)",   label: "Available" },
  ringing:   { color: "#F59E0B", bg: "rgba(245,158,11,0.10)",  label: "Ringing"   },
  on_call:   { color: "#4F46E5", bg: "rgba(79,70,229,0.10)",   label: "On Call"   },
  // The agent's own choice (Stage 4.8f.3, F2): not rung for customers.
  // (hex = the --warning / --danger tokens: this palette goes through alpha(), which reads no CSS variable)
  break:     { color: "#E8930C", bg: "rgba(232,147,12,0.10)", label: "On a break" },
  dnd:       { color: "#E5484D", bg: "rgba(229,72,77,0.10)",  label: "Do not disturb" },
};

// ── Time formatters ───────────────────────────────────────────
function fmtWaitSec(secs) {
  if (!secs) return "0s";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// durationSec + interpolation strategy — self-correcting across ETag/304 responses.
function fmtLiveDuration(sinceMs, durationSec, receivedAt, clockOffset, _tick) {
  if (durationSec != null && durationSec >= 0) {
    const secs = Math.max(0, Math.floor(durationSec + (Date.now() - receivedAt) / 1000));
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }
  if (!sinceMs) return "—";
  const secs = Math.max(0, Math.floor((Date.now() + clockOffset - sinceMs) / 1000));
  const m    = Math.floor(secs / 60);
  const s    = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtIdleSince(sinceMs, clockOffset, _tick) {
  if (!sinceMs) return "—";
  const secs = Math.max(0, Math.floor((Date.now() + clockOffset - sinceMs) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  return `${m}m`;
}

function fmtCallerWait(enqueuedAtMs, clockOffset, _tick) {
  if (!enqueuedAtMs) return "—";
  const secs = Math.max(0, Math.floor((Date.now() + clockOffset - enqueuedAtMs) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// Compute live longest wait (seconds) for a queue's caller list.
function queueMaxWaitSec(callers, clockOffset) {
  if (!callers?.length) return 0;
  return callers.reduce((max, c) => {
    const secs = c.enqueuedAt
      ? Math.max(0, Math.floor((Date.now() + clockOffset - c.enqueuedAt) / 1000))
      : (c.waitingSec || 0);
    return Math.max(max, secs);
  }, 0);
}

// ── Stat card ─────────────────────────────────────────────────
function LiveStatCard({ label, value, color, loading }) {
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  return (
    <Box sx={{
      p: 2, borderRadius: "14px",
      background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "none",
      display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0,
    }}>
      <Typography sx={{
        fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em", color: "text.disabled",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width="55%" height={32} />
      ) : (
        <Typography sx={{
          fontSize: "1.625rem", fontWeight: 800, lineHeight: 1,
          letterSpacing: "-0.03em", color: color || "text.primary",
          fontVariantNumeric: "tabular-nums",
        }}>
          {value ?? "–"}
        </Typography>
      )}
    </Box>
  );
}

const AGENT_STATUS_TKEY = { offline: "statusOffline", available: "statusAvailable", ringing: "statusRinging", on_call: "statusOnCall", break: "statusOnBreak", dnd: "statusDnd" };

// ── Agent card ────────────────────────────────────────────────
function AgentCard({ ag, receivedAt, clockOffset, tick }) {
  const { t }    = useTranslation("liveNowBand");
  const { mode } = useColorMode();
  const isLight  = mode === "light";
  const cfg      = AGENT_STATUS_CFG[ag.status] || AGENT_STATUS_CFG.offline;

  const durationDisplay =
    (ag.status === "on_call" || ag.status === "ringing")
      ? fmtLiveDuration(ag.since, ag.durationSec, receivedAt, clockOffset, tick)
      : ag.status === "available"
      ? `${fmtIdleSince(ag.since, clockOffset, tick)} ${t("idle")}`
      : null;

  const durationColor =
    ag.status === "on_call"   ? "#4F46E5" :
    ag.status === "ringing"   ? "#F59E0B" :
    ag.status === "available" ? "text.secondary" : "text.disabled";

  return (
    <Box sx={{
      width: { xs: "calc(50% - 6px)", sm: 158 },
      borderRadius: "12px",
      background: "var(--surface-card)",
      border: `1.5px solid ${alpha(cfg.color, ag.status === "offline" ? 0.15 : 0.35)}`,
      boxShadow: "none",
      p: 1.5,
      display: "flex", flexDirection: "column", gap: 0.5,
      transition: "border-color 0.3s",
    }}>
      {/* Status dot + extension */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{
          fontFamily: "monospace", fontWeight: 800, fontSize: "0.875rem",
          color: "text.primary", letterSpacing: "0.02em",
        }}>
          {ag.extension}
        </Typography>
        <Box sx={{
          width: 8, height: 8, borderRadius: "50%", background: cfg.color, flexShrink: 0,
          animation: (ag.status === "on_call" || ag.status === "ringing")
            ? "agPulse 1.4s ease-in-out infinite" : "none",
          "@keyframes agPulse": {
            "0%,100%": { opacity: 1, transform: "scale(1)" },
            "50%":     { opacity: 0.3, transform: "scale(0.7)" },
          },
        }} />
      </Box>

      {/* Section */}
      <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.2 }}>
        {ag.section || "—"}
      </Typography>

      {/* Status label */}
      <Typography sx={{
        fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.06em", color: cfg.color, mt: 0.25,
      }}>
        {t(AGENT_STATUS_TKEY[ag.status] || "statusOffline", { defaultValue: cfg.label })}
      </Typography>

      {/* Duration / idle */}
      {durationDisplay && (
        <Typography sx={{
          fontSize: "0.75rem", fontVariantNumeric: "tabular-nums",
          fontWeight: 700, color: durationColor,
        }}>
          {durationDisplay}
        </Typography>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────
export default function LiveNowBand() {
  const { t }   = useTranslation("liveNowBand");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [data,       setData]       = useState(null);
  const [agentsList, setAgentsList] = useState(null);
  const [liveStatus, setLiveStatus] = useState("loading");
  const [tick,       setTick]       = useState(0);
  const [alertDismissed, setAlertDismissed] = useState(false);

  const etagRef        = useRef(null);
  const agentsEtagRef  = useRef(null);
  const agentsRcvAtRef = useRef(Date.now());
  const clockOffsetRef = useRef(0);

  const poll = useCallback(async () => {
    try {
      const reqHeaders = {};
      if (etagRef.current) reqHeaders["If-None-Match"] = etagRef.current;
      const res = await axiosInstance.get("/live/summary", {
        headers: reqHeaders,
        validateStatus: (s) => s === 304 || (s >= 200 && s < 300),
      });
      const svt = parseInt(res.headers["x-server-time"] || "", 10);
      if (!isNaN(svt)) clockOffsetRef.current = svt - Date.now();
      if (res.status === 304) return;
      const etag = res.headers["etag"];
      if (etag) etagRef.current = etag;
      setData(res.data);
      setLiveStatus("ok");
    } catch {
      setLiveStatus("error");
    }
  }, []);

  const pollAgents = useCallback(async () => {
    try {
      const reqHeaders = {};
      if (agentsEtagRef.current) reqHeaders["If-None-Match"] = agentsEtagRef.current;
      const res = await axiosInstance.get("/live/agents", {
        headers: reqHeaders,
        validateStatus: (s) => s === 304 || (s >= 200 && s < 300),
      });
      const svt = parseInt(res.headers["x-server-time"] || "", 10);
      if (!isNaN(svt)) clockOffsetRef.current = svt - Date.now();
      if (res.status === 304) return;
      const etag = res.headers["etag"];
      if (etag) agentsEtagRef.current = etag;
      agentsRcvAtRef.current = Date.now();
      setAgentsList(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    poll(); pollAgents();
    let id = setInterval(() => { poll(); pollAgents(); }, POLL_MS);
    const onVisibility = () => {
      if (document.hidden) { clearInterval(id); }
      else { poll(); pollAgents(); id = setInterval(() => { poll(); pollAgents(); }, POLL_MS); }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisibility); };
  }, [poll, pollAgents]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const agents = agentsList?.agents || data?.agents || [];
    const hasActiveCall = agents.some((ag) => ag.status === "on_call" || ag.status === "ringing");
    if (!hasActiveCall) return;
    const guard = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [agentsList, data]);

  // Re-show alert when wait crosses back up above threshold
  useEffect(() => { setAlertDismissed(false); }, []);

  const isLoading   = liveStatus === "loading" && data === null;
  const totals      = data?.totals  || {};
  const queues      = data?.queues  || [];
  const agents      = agentsList?.agents || data?.agents || [];
  const clockOffset = clockOffsetRef.current;
  const receivedAt  = agentsRcvAtRef.current;

  const longestWait = totals.longestWaitSec ?? 0;
  const waitColor   =
    longestWait >= WAIT_DANGER_SEC  ? "#EF4444" :
    longestWait >= WAIT_WARNING_SEC ? "#F59E0B" : "#22C55E";

  // Find which section has the longest-waiting caller for the alert message
  const alertSection = queues.reduce((worst, q) => {
    const w = queueMaxWaitSec(q.callers, clockOffset);
    return w > (worst?.wait ?? 0) ? { section: q.section, wait: w } : worst;
  }, null);

  const showAlert = !alertDismissed && longestWait >= WAIT_WARNING_SEC;

  return (
    <Box sx={{
      borderRadius: "18px",
      border: `1px solid ${isLight ? "rgba(79,70,229,0.18)" : "rgba(99,102,241,0.22)"}`,
      background: isLight ? "rgba(238,242,255,0.55)" : "rgba(22,32,50,0.65)",
      boxShadow: "none",
      p: { xs: 2, sm: 2.5 },
      mb: 3,
    }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: showAlert ? 1.75 : 2.5 }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: liveStatus === "error" ? "#F59E0B" : "#22C55E",
          animation: liveStatus !== "error" ? "livePulse 1.4s ease-in-out infinite" : "none",
          "@keyframes livePulse": {
            "0%,100%": { opacity: 1, transform: "scale(1)" },
            "50%":     { opacity: 0.3, transform: "scale(0.75)" },
          },
        }} />
        <Typography sx={{
          fontWeight: 800, fontSize: "0.8125rem", textTransform: "uppercase",
          letterSpacing: "0.07em", color: "text.primary",
        }}>
          {t("liveNow")}
        </Typography>
        {liveStatus === "error" && (
          <Chip label={t("reconnecting")} size="small" sx={{
            height: 20, fontSize: "0.6875rem", fontWeight: 600,
            color: "#F59E0B", background: "rgba(245,158,11,0.1)",
            border: "1px solid rgba(245,158,11,0.25)",
            "& .MuiChip-label": { px: 1 },
          }} />
        )}
      </Box>

      {/* ── Threshold alert banner ── */}
      {showAlert && (
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 1, mb: 2,
          px: 1.5, py: 0.875, borderRadius: "10px",
          background: alpha(waitColor, 0.1),
          border: `1px solid ${alpha(waitColor, 0.3)}`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: waitColor, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: waitColor }}>
              {longestWait >= WAIT_DANGER_SEC ? t("criticalWait") : t("longWait")}
              {alertSection ? ` — ${alertSection.section}` : ""}:{" "}
              {t("oldestCallerWaiting")} <strong>{fmtWaitSec(longestWait)}</strong>
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={() => setAlertDismissed(true)}
            sx={{
              background: "none", border: "none", cursor: "pointer",
              color: waitColor, opacity: 0.7, fontFamily: "inherit",
              fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
              "&:hover": { opacity: 1 },
            }}
          >
            ✕
          </Box>
        </Box>
      )}

      {/* ── Stat strip ── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
        gap: 1.5,
      }}>
        <LiveStatCard label={t("statCallsWaiting")}  value={isLoading ? undefined : (totals.callsWaiting    ?? 0)} loading={isLoading} />
        <LiveStatCard label={t("statLongestWait")}   value={isLoading ? undefined : fmtWaitSec(longestWait)} color={isLoading ? undefined : waitColor} loading={isLoading} />
        <LiveStatCard label={t("statAgentsOnline")}  value={isLoading ? undefined : (totals.agentsOnline    ?? 0)} loading={isLoading} />
        <LiveStatCard label={t("statAvailable")}     value={isLoading ? undefined : (totals.agentsAvailable ?? 0)} color={isLoading ? undefined : "#22C55E"} loading={isLoading} />
        <LiveStatCard label={t("statOnCall")}        value={isLoading ? undefined : (totals.agentsOnCall    ?? 0)} color={isLoading ? undefined : "#4F46E5"} loading={isLoading} />
      </Box>

      {/* ── Queue tiles ── */}
      {!isLoading && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: "0.6875rem", textTransform: "uppercase",
            letterSpacing: "0.07em", color: "text.disabled", mb: 1.25,
          }}>
            {t("queueHeader")}
          </Typography>

          {queues.length === 0 ? (
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.875,
              py: 1, px: 1.5, borderRadius: "10px",
              background: alpha("#22C55E", 0.07),
              border: `1px solid ${alpha("#22C55E", 0.18)}`,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#22C55E", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#22C55E" }}>
                {t("noCallersWaiting")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {queues.map((q) => {
                const qMax     = queueMaxWaitSec(q.callers, clockOffset);
                const qColor   = qMax >= WAIT_DANGER_SEC ? "#EF4444" : qMax >= WAIT_WARNING_SEC ? "#F59E0B" : "#22C55E";
                return (
                  <Box key={q.section} sx={{
                    minWidth: 190, flex: "0 0 auto",
                    borderRadius: "12px",
                    background: "var(--surface-card)",
                    border: "1px solid var(--line-200)",
                    boxShadow: "none",
                    overflow: "hidden",
                  }}>
                    {/* Queue header */}
                    <Box sx={{
                      px: 1.5, py: 0.875,
                      borderBottom: "1px solid var(--line-200)",
                      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                      background: alpha("#4F46E5", 0.04),
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", pt: 0.125 }}>
                        {q.section}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
                        <Chip label={t("nWaiting", { count: q.waiting })} size="small" sx={{
                          height: 18, fontSize: "0.6rem", fontWeight: 700,
                          color: "#EF4444", background: "rgba(239,68,68,0.1)",
                          "& .MuiChip-label": { px: 0.75 },
                        }} />
                        {qMax > 0 && (
                          <Typography sx={{
                            fontSize: "0.6rem", fontWeight: 700, color: qColor,
                            fontVariantNumeric: "tabular-nums",
                          }}>
                            {t("maxWait", { time: fmtWaitSec(qMax) })}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Caller rows */}
                    {(q.callers || []).map((caller) => (
                      <Box key={caller.uniqueId} sx={{
                        px: 1.5, py: 0.75,
                        display: "flex", alignItems: "center", gap: 1.25,
                        "&:not(:last-child)": { borderBottom: "1px solid var(--line-100)" },
                      }}>
                        <Typography sx={{
                          fontSize: "0.6875rem", fontWeight: 800, color: "text.disabled",
                          fontVariantNumeric: "tabular-nums", minWidth: 18, flexShrink: 0,
                        }}>
                          #{caller.position}
                        </Typography>
                        <Typography sx={{
                          fontSize: "0.8125rem", fontFamily: "monospace", fontWeight: 600,
                          flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {caller.callerId}
                        </Typography>
                        <Typography sx={{
                          fontSize: "0.75rem", fontWeight: 700, color: "#EF4444",
                          fontVariantNumeric: "tabular-nums", minWidth: 44, textAlign: "right", flexShrink: 0,
                        }}>
                          {fmtCallerWait(caller.enqueuedAt, clockOffset, tick)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── Agent status card grid ── */}
      {!isLoading && agents.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: "0.6875rem", textTransform: "uppercase",
            letterSpacing: "0.07em", color: "text.disabled", mb: 1.25,
          }}>
            {t("agentsHeader", { count: agents.length })}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            {agents.map((ag) => (
              <AgentCard
                key={ag.extension}
                ag={ag}
                receivedAt={receivedAt}
                clockOffset={clockOffset}
                tick={tick}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
